import React from 'react';
import CompletePhaseButtonPlate from '../../Header/CompletePhaseButton/CompletePhaseButtonPlate';
import {getCompleteButtonView} from '../../../utils/gameUi/getCompleteButtonView';
import {getCompleteButtonColor} from '../../../utils/gameUi/getCompleteButtonColor';
import {GAME_UI_PHASES} from '../../../utils/gameUi/getGameUiPhase';
import {PHASES, WAR_PHASE_STAGES} from '../../../constants/phases';

// Кнопка хода в витрине: та же плита, что в матче (CompletePhaseButtonPlate),
// с видом и цветом из тех же чистых функций — но на примерах, без партии

const KIT_PHASE_TO_PHASE = {
  [GAME_UI_PHASES.PREP]: PHASES.SETUP,
  [GAME_UI_PHASES.SUPPLY]: PHASES.SUPPLY,
  [GAME_UI_PHASES.CONSTRUCTION]: PHASES.CONSTRUCTION,
  [GAME_UI_PHASES.WAR]: PHASES.WAR,
  [GAME_UI_PHASES.END]: PHASES.WAR,
};

// Этапы, на которых дела не считаются: кнопка показывает этап без счетчика
const NO_COUNTER_STAGES = [WAR_PHASE_STAGES.RECRUITMENT, WAR_PHASE_STAGES.COMBAT];

const KIT_TIMER_FRACTION = 0.6;

const progress = (done, total, actionableCount) => ({
  done,
  total,
  actionableIds: Array.from({length: actionableCount}, (item, index) => `kit-${index}`),
});
const NO_COUNTER = {done: null, total: null, actionableIds: []};

const KitTimerText = ({children, isStacked = false}) => (
  <div className={`complete-phase-button-timer-text-container${isStacked ? ' complete-phase-button-timer-text-container_stacked' : ''}`}>
    {children.map((text) => <span key={text} className={'complete-phase-button-timer-text'}>{text}</span>)}
  </div>
);

/**
 * Примеры кнопки хода для фазы и этапа витрины: свой ход во всех состояниях,
 * ход соперника и «время вышло»
 * @returns {Array<{id: string, label: string, viewParams: Object, isOwnTurn: boolean,
 *   isTimer?: boolean, isLocked?: boolean}>}
 */
export const getKitCompleteButtonSamples = (kitPhase, warStage) => {
  const phase = KIT_PHASE_TO_PHASE[kitPhase];
  const warPhaseStage = phase === PHASES.WAR ? warStage : null;
  const base = {phase, warPhaseStage};

  if (kitPhase === GAME_UI_PHASES.END) {
    return [{id: 'ended', label: 'Конец партии', viewParams: {...base, gameResults: [{}]}, isOwnTurn: false}];
  }

  const own = (id, label, progressValue, extra = {}) => ({
    id, label, isOwnTurn: true, viewParams: {...base, isOwnTurn: true, progress: progressValue}, ...extra,
  });

  let ownSamples;
  if (phase === PHASES.SETUP) {
    ownSamples = [
      own('pending', 'Свой ход', NO_COUNTER),
      {
        id: 'ready', label: 'Квартал поставлен', isOwnTurn: true,
        viewParams: {...base, isOwnTurn: true, isStartingQuarterPlaced: true, progress: NO_COUNTER},
      },
    ];
  } else if (warPhaseStage === WAR_PHASE_STAGES.COMBAT) {
    ownSamples = [
      {
        id: 'confirm', label: 'Бой: подтвердить', isOwnTurn: true,
        viewParams: {...base, isOwnTurn: true, isCombatConfirmPending: true, progress: NO_COUNTER},
      },
      {
        id: 'confirm-reroll', label: 'Бой: есть переброс', isOwnTurn: true,
        viewParams: {
          ...base, isOwnTurn: true, isCombatConfirmPending: true, isCombatRerollPending: true, progress: NO_COUNTER,
        },
      },
    ];
  } else if (NO_COUNTER_STAGES.includes(warPhaseStage)) {
    ownSamples = [own('pending', 'Свой ход', NO_COUNTER)];
  } else {
    ownSamples = [
      own('start', 'Начало этапа', progress(0, 5, 5)),
      own('pending', 'Есть дела, таймер', progress(3, 5, 2), {isTimer: true}),
      own('two-digit', 'Двузначное', progress(20, 24, 4)),
      own('ready', 'Готово', progress(5, 5, 0)),
      own('ready-short', 'Готово, N < S', progress(4, 5, 0), {isTimer: true}),
    ];
  }

  return [
    ...ownSamples,
    {id: 'waiting', label: 'Ждите', isOwnTurn: false, viewParams: {...base, isOwnTurn: false, progress: NO_COUNTER}},
    {
      id: 'locked', label: 'Время вышло', isOwnTurn: true, isLocked: true,
      viewParams: {...base, isOwnTurn: true, isTurnInputLocked: true, progress: NO_COUNTER},
    },
  ];
};

/**
 * Одна кнопка хода витрины
 * @param {Object} props.sample - пример из getKitCompleteButtonSamples
 * @param {Object} props.me - свой игрок витрины
 * @param {Object} props.rival - соперник, которого ждем
 */
export const KitCompleteButton = ({sample, me, rival}) => {
  const view = getCompleteButtonView(sample.viewParams);
  const buttonColor = getCompleteButtonColor({
    ...sample.viewParams,
    turnPlayer: sample.isOwnTurn ? me : rival,
    warPhasePlayer: sample.isOwnTurn ? me : rival,
    ownPlayerId: me.id,
  });
  let timerText = null;
  if (sample.isLocked) {
    timerText = <KitTimerText isStacked>{['время', 'вышло']}</KitTimerText>;
  } else if (sample.isTimer) {
    timerText = <KitTimerText>{['36', 'сек']}</KitTimerText>;
  }

  return (
    <CompletePhaseButtonPlate
      view={view}
      buttonColor={buttonColor}
      displayedPlayer={sample.isOwnTurn ? me : rival}
      timerText={timerText}
      isTimerShown={Boolean(sample.isTimer || sample.isLocked)}
      timeLeftFraction={sample.isLocked ? 0 : KIT_TIMER_FRACTION}
      cursor={sample.isLocked ? 'default' : 'pointer'}
      isTutorialWaiting={!sample.isOwnTurn}
    />
  );
};
